import { createApi, createBaseQuery, createGlobalInvalidationTag } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    type OpenApiBatchUpdateCharacteristicGroupsRequest,
    type OpenApiBatchUpdateCharacteristicGroupsResponseSuccess,
    OpenApiBatchUpdateCharacteristicGroupsUrl,
    type OpenApiCreateCharacteristicGroupRequest,
    type OpenApiCreateCharacteristicGroupResponseSuccess,
    OpenApiCreateCharacteristicGroupUrl,
    type OpenApiCreateCharacteristicRequest,
    type OpenApiCreateCharacteristicResponseSuccess,
    type OpenApiDeleteCharacteristicGroupsRequest,
    type OpenApiDeleteCharacteristicGroupsResponseSuccess,
    OpenApiDeleteCharacteristicGroupsUrl,
    type OpenApiGetCharacteristicGroupsWithCharacteristicsResponseSuccess,
    type OpenApiGetCharacteristicGroupsWithCharacteristicsSearchParams,
    OpenApiGetCharacteristicGroupsWithCharacteristicsUrl,
    OpenApiGetCharacteristicsCountMethod,
    type OpenApiGetCharacteristicsCountRequest,
    type OpenApiGetCharacteristicsCountResponseSuccess,
    OpenApiGetCharacteristicsCountUrl,
    type OpenApiGetCharacteristicsResponseSuccess,
    type OpenApiGetCharacteristicsSearchParams,
    type OpenApiGetCharacteristicsStatsRequest,
    type OpenApiGetCharacteristicsStatsResponseSuccess,
    type OpenApiGetCharacteristicValuesResponseSuccess,
    type OpenApiGetCharacteristicValuesSearchParams,
    type OpenApiMoveCharacteristicsToGroupPathParameters,
    type OpenApiMoveCharacteristicsToGroupRequest,
    type OpenApiMoveCharacteristicsToGroupResponseSuccess,
    type OpenApiMoveCharacteristicsToNewGroupRequest,
    type OpenApiMoveCharacteristicsToNewGroupResponseSuccess,
    OpenApiMoveCharacteristicsToNewGroupUrl,
    type OpenApiSearchCharacteristicsResponseSuccess,
    type OpenApiSearchCharacteristicsSearchParams,
    type OpenApiUpdateCharacteristicGroupPathParameters,
    type OpenApiUpdateCharacteristicGroupRequest,
    type OpenApiUpdateCharacteristicGroupResponseSuccess,
    type OpenApiUpdateCharacteristicRequest,
    type OpenApiUpdateCharacteristicResponseSuccess,
    type OpenApiUpdateCharacteristicSettingsBatchRequest,
    type OpenApiUpdateCharacteristicSettingsBatchResponseSuccess,
    OpenApiUpdateCharacteristicsMetaBatchMethod,
    type OpenApiUpdateCharacteristicsMetaBatchRequest,
    type OpenApiUpdateCharacteristicsMetaBatchResponseSuccess,
    OpenApiUpdateCharacteristicsMetaBatchUrl,
} from '#shared/api/schema';
import { InvalidateGridApiHelper } from '#shared/lib/requests';
import { enqueueCharacteristicSequencePatch } from './characteristicSequencePatchQueue';

const characteristicsGetTag = createGlobalInvalidationTag('CharacteristicsGet');

const CharacteristicTag = 'Characteristic' as const;

const GroupTag = 'GroupsList' as const;

export const GroupsListTag = 'GroupsList' as const;

// Костыль, чтобы инвалидировать все запросы /search кроме последнего (текущего)
// Совместно с useSyncedState в CharacteristicsActive.tsx позволяет избавиться от состояния загрузки,
// избегая сложных манипуляций с queryData
const characteristicsGridTagInvalidator = new InvalidateGridApiHelper<
    typeof CharacteristicTag,
    OpenApiSearchCharacteristicsSearchParams
>(CharacteristicTag, 'characteristicSearch');

const characteristicsGroupsGridTagInvalidator = new InvalidateGridApiHelper<
    typeof GroupTag,
    OpenApiSearchCharacteristicsSearchParams
>(GroupTag, 'groupSearch');

export const api = createApi({
    name: 'entities/characteristic/api/api',
    baseQuery: createBaseQuery(apiV1Request),
    tagTypes: [characteristicsGetTag, CharacteristicTag, GroupsListTag],
    endpoints: (builder) => {
        return {
            getAll: builder.query<OpenApiGetCharacteristicsResponseSuccess, OpenApiGetCharacteristicsSearchParams>({
                query: ({ product_id }) => {
                    return {
                        url: '/characteristics',
                        params: { product_id },
                        method: 'GET',
                    };
                },
                /**
                 * Читаем тег для инвалидации кэша по полям варианта товара, чтобы при переходе на другую карточку подтягивались свежие характеристики
                 */
                providesTags: [characteristicsGetTag],
                keepUnusedDataFor: 2147483,
            }),
            search: builder.query<
                OpenApiSearchCharacteristicsResponseSuccess,
                OpenApiSearchCharacteristicsSearchParams
            >({
                query: (params) => {
                    return {
                        url: '/characteristics/search',
                        params,
                        method: 'GET',
                    };
                },
                keepUnusedDataFor: 0,
                providesTags: (result, error, args) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        characteristicsGetTag,
                        ...characteristicsGridTagInvalidator.provideHashTagForGridEndpoint(args),
                    ];
                },
            }),
            getCharacteristicsCount: builder.query<
                OpenApiGetCharacteristicsCountResponseSuccess,
                OpenApiGetCharacteristicsCountRequest
            >({
                query: () => {
                    return {
                        url: OpenApiGetCharacteristicsCountUrl,
                        method: OpenApiGetCharacteristicsCountMethod,
                    };
                },
                providesTags: [characteristicsGetTag, GroupsListTag],
            }),
            getValuesBySlug: builder.query<
                OpenApiGetCharacteristicValuesResponseSuccess,
                OpenApiGetCharacteristicValuesSearchParams
            >({
                query: ({ search_text, product_id }) => {
                    return {
                        url: '/characteristics/' + search_text + '/values',
                        params: { product_id },
                        method: 'GET',
                    };
                },
                keepUnusedDataFor: 2147483,
            }),
            edit: builder.mutation<OpenApiUpdateCharacteristicResponseSuccess, OpenApiUpdateCharacteristicRequest>({
                query: (payload) => {
                    return {
                        url: '/characteristics',
                        method: 'PUT',
                        body: payload,
                    };
                },
                /**
                 * Делаем инвалидацию кэша по характеристикам после сохранения новых полей,
                 * чтобы при переходе на другую карточку подтягивались актуальные характеристики
                 */
                invalidatesTags: (response) => (response ? [characteristicsGetTag, GroupsListTag] : []),
            }),
            save: builder.mutation<OpenApiCreateCharacteristicResponseSuccess, OpenApiCreateCharacteristicRequest>({
                query: (payload) => {
                    return {
                        url: '/characteristics',
                        method: 'POST',
                        body: payload,
                    };
                },
                /**
                 * Делаем инвалидацию кэша по характеристикам после сохранения новых полей,
                 * чтобы при переходе на другую карточку подтягивались актуальные характеристики
                 */
                invalidatesTags: (response) => (response ? [characteristicsGetTag] : []),
            }),
            getStats: builder.query<
                OpenApiGetCharacteristicsStatsResponseSuccess,
                OpenApiGetCharacteristicsStatsRequest
            >({
                query: (payload) => {
                    return {
                        url: '/characteristics/stats',
                        method: 'POST',
                        body: payload,
                    };
                },
                transformResponse: (
                    response: OpenApiGetCharacteristicsStatsResponseSuccess
                ): OpenApiGetCharacteristicsStatsResponseSuccess => {
                    return {
                        ...response,
                        //characteristics может приходить null
                        characteristics: response.characteristics || [],
                    };
                },
            }),
            postSettingsBatch: builder.mutation<
                OpenApiUpdateCharacteristicSettingsBatchResponseSuccess,
                OpenApiUpdateCharacteristicSettingsBatchRequest
            >({
                query: (payload) => {
                    return {
                        url: '/characteristics/settings/batch',
                        method: 'POST',
                        body: payload,
                    };
                },
            }),
            patchBatch: builder.mutation<
                OpenApiUpdateCharacteristicsMetaBatchResponseSuccess,
                OpenApiUpdateCharacteristicsMetaBatchRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiUpdateCharacteristicsMetaBatchUrl,
                        method: OpenApiUpdateCharacteristicsMetaBatchMethod,
                        body: payload,
                    };
                },

                invalidatesTags: (response) =>
                    response
                        ? [
                              ...characteristicsGridTagInvalidator.getTagsToInvalidate(),
                              ...characteristicsGroupsGridTagInvalidator.getTagsToInvalidate(),
                              GroupsListTag,
                          ]
                        : [],
            }),
            patchSequenceBatch: builder.mutation<
                OpenApiUpdateCharacteristicsMetaBatchResponseSuccess,
                OpenApiUpdateCharacteristicsMetaBatchRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiUpdateCharacteristicsMetaBatchUrl,
                        method: OpenApiUpdateCharacteristicsMetaBatchMethod,
                        body: payload,
                    };
                },
            }),
            getGroups: builder.query<
                OpenApiGetCharacteristicGroupsWithCharacteristicsResponseSuccess,
                OpenApiGetCharacteristicGroupsWithCharacteristicsSearchParams
            >({
                query: (params) => {
                    return {
                        url: OpenApiGetCharacteristicGroupsWithCharacteristicsUrl,
                        params,
                        method: 'GET',
                    };
                },
                providesTags: (result, error, args) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        GroupsListTag,
                        ...characteristicsGroupsGridTagInvalidator.provideHashTagForGridEndpoint(args),
                    ];
                },
                // Добавляем инвалидацию бейджей, они могут обновиться при импорте из внешних источников, Excel, Ozon
                keepUnusedDataFor: 5,
            }),
            createGroup: builder.mutation<
                OpenApiCreateCharacteristicGroupResponseSuccess,
                OpenApiCreateCharacteristicGroupRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiCreateCharacteristicGroupUrl,
                        method: 'POST',
                        body: payload,
                    };
                },

                invalidatesTags: [GroupsListTag],
            }),
            updateGroup: builder.mutation<
                OpenApiUpdateCharacteristicGroupResponseSuccess,
                {
                    params: OpenApiUpdateCharacteristicGroupPathParameters;
                    payload: OpenApiUpdateCharacteristicGroupRequest;
                }
            >({
                query: ({ params: { id }, payload }) => {
                    return {
                        url: `/characteristics/groups/${id}`,
                        method: 'PATCH',
                        body: payload,
                    };
                },

                invalidatesTags: [GroupsListTag],
            }),
            groupPatchBatch: builder.mutation<
                OpenApiBatchUpdateCharacteristicGroupsResponseSuccess,
                OpenApiBatchUpdateCharacteristicGroupsRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiBatchUpdateCharacteristicGroupsUrl,
                        method: 'PATCH',
                        body: payload,
                    };
                },

                invalidatesTags: (response) =>
                    response ? characteristicsGroupsGridTagInvalidator.getTagsToInvalidate() : [],
            }),

            moveToGroup: builder.mutation<
                OpenApiMoveCharacteristicsToGroupResponseSuccess,
                {
                    params: OpenApiMoveCharacteristicsToGroupPathParameters;
                    payload: OpenApiMoveCharacteristicsToGroupRequest;
                }
            >({
                query: ({ params: { group_id }, payload }) => {
                    return {
                        url: `/characteristics/groups/${group_id}/move`,
                        method: 'POST',
                        body: payload,
                    };
                },

                invalidatesTags: [GroupsListTag],
            }),
            deleteGroups: builder.mutation<
                OpenApiDeleteCharacteristicGroupsResponseSuccess,
                OpenApiDeleteCharacteristicGroupsRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiDeleteCharacteristicGroupsUrl,
                        method: 'DELETE',
                        body: payload,
                    };
                },

                invalidatesTags: [GroupsListTag],
            }),
            moveToNewGroup: builder.mutation<
                OpenApiMoveCharacteristicsToNewGroupResponseSuccess,
                OpenApiMoveCharacteristicsToNewGroupRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiMoveCharacteristicsToNewGroupUrl,
                        method: 'POST',
                        body: payload,
                    };
                },

                invalidatesTags: [GroupsListTag],
            }),
        };
    },
});

export const useCharacteristicsGetAll = api.useGetAllQuery;
export const useCharacteristicsSearch = api.useSearchQuery;
export const useGetCharacteristicsCountQuery = api.useGetCharacteristicsCountQuery;
export const useGetCharacteristicValuesBySlug = api.endpoints.getValuesBySlug.useQuery;
export const useLazyGetCharacteristicsStats = api.useLazyGetStatsQuery;
export const editCharacteristic = api.endpoints.edit.mutate;
export const saveCharacteristic = api.endpoints.save.mutate;
export const postCharacteristicSettingsBatch = api.endpoints.postSettingsBatch.mutate;
export const patchCharacteristicBatch = api.endpoints.patchBatch.mutate;

const patchCharacteristicSequenceBatch = api.endpoints.patchSequenceBatch.mutate;

export function patchCharacteristicSequenceOptimistically({
    items,
    searchQueryArgs,
}: OpenApiUpdateCharacteristicsMetaBatchRequest & {
    searchQueryArgs: OpenApiSearchCharacteristicsSearchParams;
}) {
    api.endpoints.search.updateQueryData(searchQueryArgs, (draft) => {
        const updateMap = new Map(items.map((item) => [item.id, item.display_sequence]));

        for (const draftItem of draft.items) {
            const displaySequence = updateMap.get(draftItem.id);

            if (displaySequence !== undefined) {
                draftItem.display_sequence = displaySequence;
            }
        }
    });

    return enqueueCharacteristicSequencePatch(
        () => patchCharacteristicSequenceBatch({ items }),
        (hasError) => {
            if (hasError) {
                api.util.invalidateTags([characteristicsGetTag]);
            }
        }
    );
}

export const useGetCharacteristicsGroupsQuery = api.useGetGroupsQuery;
export const createCharacteristicGroup = api.endpoints.createGroup.mutate;
export const updateCharacteristicGroup = api.endpoints.updateGroup.mutate;
export const patchCharacteristicGroupsBatch = api.endpoints.groupPatchBatch.mutate;
export const moveCharacteristicsToGroup = api.endpoints.moveToGroup.mutate;
export const deleteCharacteristicGroups = api.endpoints.deleteGroups.mutate;
export const moveCharacteristicsToNewGroup = api.endpoints.moveToNewGroup.mutate;

export const invalidateCharacteristicsGrid = () => {
    api.util.invalidateTags([characteristicsGetTag]);
};
